// Translation object for bilingual support (English/Ukrainian)
export const translations = {
  en: {
    hero: {
      badge: "AVAILABLE NOW",
      title: "Comfortable Smart\nLiving in Lviv",
      subtitle: "Urban living near the city center in a quiet location, with everything you need within minutes",
      tags: ["1 Bedroom + Large Studio", "Sunrise & High Castle views", "Spacious Terrace", "5 min to city center"],
      cta: "Book Private Tour",
      viewOnMap: "View on Map"
    },
    stats: {
      totalArea: "Total Area",
      ceilingHeight: "Ceiling Height",
      balcony: "Terrace",
      largeStudio: "Large Studio",
      sqm: "sqm",
      m: "m"
    },
    features: [
      { title: "In-Floor and In-Wall Heating", desc: "Radiant heating system with micro-piping", color: "from-orange-400 to-red-500" },
      { title: "Smart Home Features", desc: "Voice control, motion sensors, smart thermostats, and more", color: "from-blue-400 to-indigo-500" },
      { title: "Comprehensive Security", desc: "Professional monitoring for break-ins, flood, fire, and gas leaks", color: "from-green-400 to-emerald-500" },
      { title: "South-East Views", desc: "Sunrise views and natural light all day", color: "from-yellow-400 to-orange-500" },
      { title: "Home Office Space", desc: "Dedicated workspace setup", color: "from-purple-400 to-pink-500" },
      { title: "Underground Parking", desc: "Included spot near elevator and garage exit", color: "from-slate-400 to-gray-500" }
    ],
    neighborhood: [
      { title: "Lviv National Opera", desc: "City center & cultural hub", time: "4 min car / 14 min walk" },
      { title: "Shopping", desc: "New Point mall with Ashan, Jusk, shops and restaurants", time: "2 min walk" },
      { title: "Cafes & Restaurants", desc: "Multiple cafes, restaurants, massage salon, Nova Poshta, Ukrposhta", time: "1-5 min" },
      { title: "Public Transport", desc: "Main bus/tram routes on Chornovola street", time: "5 min walk" },
      { title: "Medical", desc: "Clinics and pharmacies", time: "3 min" },
      { title: "Fitness", desc: "Gyms and sports facilities", time: "5 min" },
      { title: "Park Vysokiy Zamok", desc: "Green space for walks and recreation", time: "13 min walk" },
      { title: "Park 700 years of Lviv", desc: "Popular park for activities", time: "18 min walk" },
      { title: "Schools", desc: "Several schools nearby", time: "5-10 min" },
      { title: "Kindergartens", desc: "3 kindergartens in the area", time: "<5 min walk" }
    ],
    buildingInfo: [
      { label: "Address", value: "пр. Чорновола 16a, Львів, (Avalon Ring 1)" },
      { label: "Building Floors", value: "9 floors" },
      { label: "Apartment Floor", value: "7th floor" },
      { label: "Building Class", value: "Business class" },
      { label: "Apartment Status", value: "Like new - We lived here about one year (95% complete, few finishing touches pending)" },
      { label: "Ceiling Height", value: "2.8m" },
      { label: "Windows", value: "Large panoramic windows facing south-east" },
      { label: "Total Apartments", value: "1000 apartments in the complex" },
      { label: "Construction", value: "Monolithic-frame construction" },
      { label: "Insulation", value: "Mineral wool insulation" },
      { label: "Heating", value: "Autonomous gas or electric" },
      { label: "Walls", value: "Ceramic block" },
      { label: "Security", value: "24/7 security and CCTV" },
      { label: "Parking", value: "Included spot near elevator and garage exit" },
      { label: "Elevator", value: "Modern high-speed elevator" }
    ],
    sections: {
      modernFeatures: {
        title: "What Makes It Special",
        subtitle: "Smart features and comfort in every detail"
      },
      premiumBuilding: {
        title: "Modern Building & Great Location",
        subtitle: "Join a growing community in one of Lviv's newest residential complexes",
        buildingExcellence: "About the Building",
        neighborhoodLife: "What's Nearby",
        rating: "Rated 4.9/5 by residents for quality of life and management"
      },
      visualTour: {
        title: "Visual Tour",
        subtitle: "Authentic photos showing real daily living",
        gridView: "Grid View",
        carouselView: "Carousel View",
        autoPlay: "Auto Play",
        stopAuto: "Stop Auto",
        noImages: "No images enabled. Admin can enable images in the admin panel."
      },
      aboutComplex: {
        title: "About the complex.",
        videoCaption: "This video was found on YouTube (Author: Apartment Sales Lviv)."
      },
      technicalPlans: {
        title: "Technical Plans",
        subtitle: "Detailed architectural and technical documentation"
      },
      apartmentDetails: {
        title: "What's Inside",
        subtitle: "Smart features for comfortable living",
        climateHeating: "Climate & Heating",
        smartHomeSecurity: "Smart Home & Security",
        bathroom: "Bathroom",
        kitchenAppliances: "Kitchen & Appliances",
        interiorDesign: "Interior & Design",
        utilitiesStorage: "Utilities & Storage",
        lifestyleWorkspace: "Lifestyle & Workspace"
      },
      thingsToKnow: {
        title: "Things to Know",
        subtitle: "Important information about the apartment systems and features",
        learnMore: "Learn more",
        showLess: "Show less",
        tabs: {
          operations: {
            label: "Apartment Operations",
            rentalApproach: {
              title: "Our Rental Approach",
              content: [
                "This apartment has been vacant for a few years, so we're not in a hurry. We're looking for the right match and willing to wait as long as needed.",
                "We're looking for responsible professionals with good references and stable work history. This was built as our own home, with care for every detail—not as a rental property.",
                "We hope tenants will treat it with the same care we would, making it feel like their own home. Minimum rental period is one year.",
                "Utilities: The owner pays all utility bills and then invoices you for reimbursement. This simplifies the process—you won't need to set up accounts with multiple providers. Utilities typically include: electricity, gas heating, water, internet, building maintenance fees, and security system monitoring. Average monthly utilities cost varies by season and usage, but expect approximately 2,000-4,000 UAH in winter (heating season) and 1,000-2,000 UAH in summer."
              ]
            },
            woodenFloors: {
              title: "Heating and Cooling System",
              content: [
                "The apartment uses radiant heating through the walls—modern technology that's energy-efficient and very comfortable.",
                "Why it's great:",
                "More usable space: No bulky radiators under windows. The heating is hidden in the walls, so you have complete freedom for furniture placement.",
                "Comfortable at lower temperatures: Heat radiates evenly from the walls, creating natural warmth. You'll feel cozy at 19-20°C, whereas traditional radiators need 22-23°C for the same comfort.",
                "Cleaner air: No hot radiators means no dust circulation from convection currents. Great for people with allergies.",
                "Lower energy bills: The system works at lower temperatures (35-40°C vs 60-70°C for radiators), using less energy.",
                "About the wooden floor:",
                "Built-in sensors automatically control floor heating (max 27°C) to prevent the wood from drying out or warping. The system manages itself—please don't adjust the floor thermostat."
              ]
            },
            bathroomWater: {
              title: "Hot Water & Spa-Like Bathroom Comfort",
              content: [
                "The bathroom feels like a warm spa year-round, thanks to a smart heating system that thinks ahead.",
                "Why you'll love it:",
                "Warm floors every time you shower—even in summer. The system uses a timer-based heating loop (morning and evening) that warms the floors, the massage bench, and the towel heater. When you step out of the shower, you're greeted by warm tiles instead of cold floors—regardless of the season or whether building heating is on.",
                "How it works:",
                "An 80-liter storage tank with dual heating (gas + electric) provides quick hot water access and works during power outages. The gas boiler heats water, while an electric element maintains temperature so hot water is always ready.",
                "The heating loop:",
                "A separate circuit draws heat from the storage tank to warm three things: the bathroom floor, the built-in heated bench, and the towel heater. Set on a timer for morning and evening use, so it's ready when you need it most.",
                "Good to know:",
                "• If hot water hasn't been used recently, wait 3-5 minutes for the gas boiler to heat the 80-liter tank",
                "• The heating loop needs warm water in the tank + power for the circulation pump",
                "• You have 80 liters of warm water reserve even without electricity or water supply",
                "Smart Ventilation:",
                "Three quiet exhaust fans monitor humidity levels and activate automatically when needed—keeping air fresh and preventing moisture buildup. The system is so quiet you barely notice it running. Manual override switches give you control when you want it. The toilet fan operates on a smart delay timer—it continues running for several minutes after you leave, ensuring complete air clearance without you having to think about it."
              ]
            },
            furnitureErgonomics: {
              title: "Thoughtful Furniture Ergonomics",
              content: [
                "The furniture was designed around real life—how two people actually use a space together, where things naturally accumulate, and what makes a home feel effortlessly organized.",
                "Morning Routine Made Easy:",
                "Two people getting ready simultaneously? No problem. The bathroom features a generously-sized sink with a wide concrete vanity providing ample elbow room for two. Cosmetics, toiletries, skincare—there's space for everything you use daily.",
                "But here's where it gets smart: A dedicated side cabinet houses everything you need occasionally but not constantly. Electric toothbrush chargers, hairdryer, styling tools—all stored upright on shelves with a built-in outlet. They're accessible but not cluttering your daily space.",
                "Below the sink, two deep drawers hold four pull-out baskets: two for fresh clothes, two for items awaiting laundry. Get dressed right out of the shower without tracking water through the apartment.",
                "Coming Home:",
                "The entry is designed around that moment you walk through the door, arms full. To the left of the entrance: a small shelf for your smart display and keys. Directly across from the door: a large shelf at the perfect height for dropping your bag or backpack. No more \"where did I put my keys?\" moments.",
                "Clean Design, Literally:",
                "Every light switch throughout the apartment—entry, hallways, bathroom, toilet—uses motion sensors. They activate automatically when you enter and turn off when you leave. Beyond convenience, this means no dirty fingerprints on light switches. A small detail that keeps everything cleaner.",
                "Kitchen That Stays Beautiful:",
                "In an open studio, the kitchen is part of your living space. We obsessed over keeping it attractive without constant tidying.",
                "The 75cm-deep counters provide generous workspace. The layout flows naturally: refrigerator to sink to prep area to cooking zone to serving area to table. No wasted movement.",
                "Daily essentials—spices, oils, frequently-used bowls and plates—sit on open lower shelves within easy reach. Everything else tucks into closed upper cabinets that extend to the ceiling. This serves two purposes: less dust accumulation (no cabinet tops collecting grime) and a streamlined look that always appears tidy.",
                "The Result:",
                "These aren't just design choices—they're daily quality-of-life improvements. Less time tidying, less mental load remembering where things go, more time enjoying your space."
              ]
            },
            electricalSystem: {
              title: "Electrical & Smart Home Systems",
              content: [
                "The apartment uses American-style outlets and switches (running on standard Ukrainian 240V). Modern look with extra functionality.",
                "Internet & Wi-Fi:",
                "Active internet with Wi-Fi router covering the whole apartment.",
                "Smart Home Control:",
                "Managed through Philips Hue, Samsung SmartThings Hub, and two Google Nest thermostats (mainly used for temperature monitoring right now).",
                "Smart Lighting:",
                "Motion sensors: Lights in the hallway, bathroom, and toilet turn on automatically when you enter and turn off when you leave.",
                "Wi-Fi switches: Main switches in the studio and bedroom connect to the smart system. They work as regular switches without Wi-Fi, but with the smart home setup you can:",
                "• Turn off all lights with voice commands",
                "• Control lights from your phone",
                "• Create automated lighting scenes",
                "Smart Lock (August Smart Lock):",
                "The front door has a smart lock that makes life easier:",
                "Auto-unlock: When you approach with your phone, the door unlocks automatically—no searching for keys.",
                "Auto-lock: The door locks itself a few seconds after you enter. You'll never forget to lock up.",
                "Extra security: There's also a traditional mechanical lock for added protection.",
                "Note: Auto-locking can be turned off if you prefer, though it works reliably.",
                "Nest Video Doorbell:",
                "Get video alerts on your phone whenever someone's at the door. You can talk to visitors even when you're away.",
                "Universal Outlets:",
                "Most outlets accept multiple plug types:",
                "• European plugs (Type C/F)",
                "• Ukrainian plugs",
                "• American plugs (Type A/B)",
                "Note: All outlets are 240V (Ukrainian standard) regardless of plug shape.",
                "Benefit: No adapters needed for American plugs (laptops, chargers, etc.)—as long as your device supports 100-240V (most modern electronics do).",
                "High-Power Outlets: Separate reinforced outlets for washing machine, water heater, and other high-power appliances with proper circuit breakers.",
                "110V Transformer: One transformer available for rare equipment that only works on American 110V/60Hz.",
                "Automation-Ready Outlets:",
                "Outlets installed on the terrace and near window areas, ready for automatic roller blinds if you want to add them later."
              ]
            }
          },
          upgrades: {
            label: "Optional Improvements",
            foodDispenser: {
              title: "Garbage Disposal Unit",
              content: [
                "The kitchen has a garbage disposal unit under the sink (grinds up organic waste). It might need servicing, but the faucet works perfectly on its own.",
                "We're happy to cover repair costs if you'd like to get it serviced and working."
              ]
            },
            washingMachine: {
              title: "Washing Machine Dryer Function",
              content: "We didn't use the dryer function because it wasn't working consistently. If you want to have it checked by a technician, we'll cover the service costs."
            },
            smartBlinds: {
              title: "Smart Roller Blinds",
              content: "Smart roller blinds can be installed on the windows in the bedroom and studio—either inside or outside. For example, the Aqara Roller Shade Driver controller costs around $60. Outlets are already installed near windows, ready for automation. We're happy to cover the installation costs if you'd like to have them installed."
            }
          },
          unfinished: {
            label: "What's Not Done Yet",
            climateControl: {
              title: "Advanced Climate Control",
              content: "We're still working on the full climate control system. The heat pump for air conditioning isn't installed yet (would provide cooling plus extra heating beyond gas). The fan coil units (AC units) built into the furniture aren't operational yet. Air recuperation, central humidifier, and liquid air reheating are installed but not connected to thermostats. This doesn't limit the apartment's functionality—heating works great, and the location makes summer very comfortable without AC."
            },
            bedroomLights: {
              title: "Bedroom Wall Lights",
              content: "Wall lights aren't installed yet—just wires ready in the wall. We're happy to cover the cost if you'd like to install them yourself and include it in the rent."
            },
            bathroomMixer: {
              title: "Bathroom Faucet Handle",
              content: "The bathroom faucet is built into the concrete wall and works perfectly, but it needs a designer handle to look finished."
            }
          }
        }
      },
      infrastructure: {
        slides: [
          {
            title: "Everything You Need Nearby",
            subtitle: "Right on site",
            description: "The Avalon complex has great commercial space with shops and services for daily life. Nova Poshta, Ukrposhta, barbershops, beauty salons, a kindergarten, and several cafes. Plus grocery stores and pharmacies—everything within walking distance.",
            location: "Infrastructure Map",
            distance: "On site"
          },
          {
            title: "City Center Access",
            subtitle: "Lviv National Opera",
            description: "Experience Lviv's culture and atmosphere. Avalon gives you modern living with easy city center access. Just 5 minutes by car and you're walking near the Lviv National Opera, right in the heart of the city's cultural scene.",
            location: "Lviv National Opera",
            distance: "1.5 kilometers"
          },
          {
            title: "New Point Mall",
            subtitle: "New Point Shopping Center",
            description: "New Point shopping center is a key part of the area, providing everything you need without leaving the neighborhood. This commercial hub complements the local infrastructure perfectly.",
            location: "New Point Shopping Center",
            distance: "100 meters"
          },
          {
            title: "Penta Business Center",
            subtitle: "Business Center",
            description: "Modern business center right in Avalon Kvartal. A stylish office space with a productive atmosphere. Great for networking or working on projects in an inspiring environment.",
            location: "Penta",
            distance: "10 meters"
          },
          {
            title: "Park 700th Anniversary",
            subtitle: "Green space",
            description: "The main park in Shevchenkivskyi district. Perfect for walks between work calls or spending time with family and friends. Great for dog walks, jogging, yoga, or just relaxing alone.",
            location: "Park 700th Anniversary",
            distance: "1.5 kilometers"
          }
        ]
      },
      contact: {
        title: "Get in Touch",
        subtitle: "Message us to schedule a viewing",
        responseTime: "Response time: ~4 hours",
        form: {
          nameLabel: "Your Name",
          namePlaceholder: "John Doe",
          emailLabel: "Your Email",
          emailPlaceholder: "john@example.com",
          contactInfoLabel: "Best way to reach you",
          contactInfoPlaceholder: "Email, phone, Telegram, Facebook, WhatsApp, etc. (e.g., john@example.com, +380 12 345 6789, @telegram_username)",
          messageLabel: "Message",
          messagePlaceholder: "We are a young couple. We have just married. We have five children, two dogs, a cat, and a fish. I (Ivan) work as a UX designer from home. My wife Anna works as my boss. We'd like to view the apartment. Please let us know when you're available...",
          submit: "Send Message",
          sending: "Sending...",
          success: "Thanks! We received your message and will get back to you soon.",
          error: "Something went wrong. Please try again or email us directly."
        }
      },
      cta: {
        title: "Interested in",
        title2: "Seeing the Apartment?",
        subtitle: "Long-term rental • Purchase option available • Available now",
        subtitle2: "Direct from owners • No agency fees",
        emailCta: "Schedule Viewing via Email",
        telegram: "Or message us on Telegram",
        verified: "Direct from Owners • No Agency Fees • Available Now"
      }
    },
    apartmentDetails: {
      climateHeating: [
        { title: "In-Floor and In-Wall Heating", desc: "Radiant heating through micro-pipes maintains comfortable temperature efficiently" },
        { title: "Smart Thermostats", desc: "Intelligent temperature control for optimal comfort" },
        { title: "Dual Mode Water Heater", desc: "Gas and electric water heating system" },
        { title: "Autonomous Gas Heating", desc: "Independent gas heating system" }
      ],
      smartHomeSecurity: [
        { title: "Safe", desc: "Built-in safe for valuables" },
        { title: "Ajax Security System", desc: "Professional monitoring for break-ins, flood protection, fire alarms, and automatic gas leak shut-off" },
        { title: "Smart Door Lock", desc: "Auto-locking smart lock with phone unlock" },
        { title: "Nest Video Doorbell", desc: "Video doorbell with phone notifications" },
        { title: "Smart Light Switches", desc: "Wi-Fi switches with motion sensors for automatic lighting" },
        { title: "Universal Outlets", desc: "EU/US compatible outlets throughout" },
        { title: "High-Speed Internet", desc: "Fiber optic connection for fast, reliable internet" }
      ],
      bathroom: [
        { title: "Spacious Bathroom", desc: "Large bathroom with walk-in shower and bathtub" },
        { title: "Heated Massage Bench", desc: "Built-in heated seating area" },
        { title: "Smart Fixtures", desc: "Thermostatic mixers, heated toilet seat, touch controls" }
      ],
      kitchenAppliances: [
        { title: "Combo Microwave Oven", desc: "Microwave with convection oven function" },
        { title: "Induction Cooktop", desc: "Modern induction cooking surface" },
        { title: "Touch-Activated Faucet", desc: "Touch the faucet to turn water on/off" },
        { title: "Drinking Water Filter", desc: "Reverse osmosis filtration system" },
        { title: "Washer/Dryer Combo", desc: "2-in-1 washing and drying machine" },
        { title: "Large Dishwasher", desc: "Spacious dishwasher" },
        { title: "Built-in Refrigerator", desc: "Integrated into custom cabinetry" }
      ],
      interiorDesign: [
        { title: "Wooden Floors", desc: "Natural wood flooring in all living areas" },
        { title: "Custom Furniture", desc: "Ergonomically designed custom furniture" },
        { title: "Fully Furnished", desc: "Quality custom furniture throughout" },
        { title: "Full-Height Mirrors", desc: "Floor-to-ceiling mirrors add space and light" },
        { title: "Easy-Care Design", desc: "Washable surfaces, no baseboards for easy cleaning" },
        { title: "Storage Room", desc: "Dedicated storage/utility space" },
        { title: "Waste Sorting Setup", desc: "Organized sorting system for recycling" },
        { title: "Home Office Desk", desc: "Built-in workspace for remote work" },
        { title: "Fitness Setup", desc: "Wall-mounted structure for pull-ups and fitness gear" },
        { title: "Large Terrace", desc: "Spacious terrace accessible from each room with irrigation for plants" },
        { title: "Quality Bed & Sofa", desc: "Bedroom has quality mattress (firm/semi-firm sides), living room has fold-out sofa" }
      ],
      utilitiesStorage: [],
      lifestyleWorkspace: []
    },
    admin: {
      login: {
        title: "Admin Access",
        placeholder: "Enter password",
        login: "Login",
        cancel: "Cancel",
        incorrectPassword: "Incorrect password"
      },
      exit: "Exit Admin",
      imageManagement: "Image Management",
      enabled: "enabled",
      total: "total",
      disable: "Disable",
      enable: "Enable",
      on: "ON",
      off: "OFF",
      videoPlaceholder: "Enter video file path (e.g., /complex_video.mp4)",
      noVideo: "No video file set. Admin can add video file path."
    },
    gallery: {
      view: "View",
      disableImage: "Disable image",
      loadMore: "Show",
      more: "more",
      showing: "Showing",
      of: "of",
      fileName: "File Name",
      comment: "Comment",
      addComment: "Add Comment",
      editComment: "Edit Comment",
      commentLine1: "Line 1",
      commentLine2: "Line 2",
      saveComment: "Save",
      deleteComment: "Delete"
    },
    lightbox: {
      close: "Close",
      previous: "Previous",
      next: "Next"
    },
    header: {
      logo: "Apartment",
      nav: {
        overview: "Overview",
        gallery: "Gallery",
        details: "Details",
        plans: "Plans",
        contact: "Contact"
      }
    },
    footer: {
      admin: "Admin",
      exitAdmin: "Exit Admin"
    },
    mobileContact: {
      telegram: "Telegram",
      email: "Email"
    },
    accessibility: {
      contactViaTelegram: "Contact via Telegram",
      previousImage: "Previous image",
      nextImage: "Next image",
      previousSlide: "Previous slide",
      nextSlide: "Next slide",
      goToSlide: "Go to slide",
      previousPlan: "Previous plan",
      nextPlan: "Next plan",
      apartmentOverview: "Apartment overview",
      telegramQrCode: "Telegram QR Code"
    }
  },
  ua: {
    hero: {
      badge: "ДОСТУПНО ЗАРАЗ",
      title: "Комфортне Розумне\nЖиття у Львові",
      subtitle: "Міське життя недалеко від центру в тихому районі, де все необхідне за кілька хвилин",
      tags: ["Спальня + Велика Студія", "Вид на світанок і Високий Замок", "Простора Тераса", "5 хв до центру"],
      cta: "Забронювати Приватний Огляд",
      viewOnMap: "Переглянути на карті"
    },
    stats: {
      totalArea: "Загальна Площа",
      ceilingHeight: "Висота Стелі",
      balcony: "Тераса",
      largeStudio: "Велика Студія",
      sqm: "м²",
      m: "м"
    },
    features: [
      { title: "Підлогове та Стінне Опалення", desc: "Променеве опалення через мікротрубки", color: "from-orange-400 to-red-500" },
      { title: "Розумний Дім", desc: "Голосове керування, датчики руху, розумні термостати", color: "from-blue-400 to-indigo-500" },
      { title: "Комплексна Безпека", desc: "Професійний моніторинг злому, затоплення, пожежі та витоку газу", color: "from-green-400 to-emerald-500" },
      { title: "Вид на Південний Схід", desc: "Вид на світанок та природне світло весь день", color: "from-yellow-400 to-orange-500" },
      { title: "Домашній Офіс", desc: "Облаштований робочий простір", color: "from-purple-400 to-pink-500" },
      { title: "Підземна Парковка", desc: "Включене місце біля ліфта та виїзду", color: "from-slate-400 to-gray-500" }
    ],
    neighborhood: [
      { title: "Львівська Національна Опера", desc: "Центр міста та культурний центр", time: "4 хв авто / 14 хв пішки" },
      { title: "Шопінг", desc: "ТРЦ New Point з Ашан, Jusk, магазинами та ресторанами", time: "2 хв пішки" },
      { title: "Кав'ярні та Ресторани", desc: "Кілька кав'ярень, ресторанів, масажний салон, Нова Пошта, Укрпошта", time: "1-5 хв" },
      { title: "Громадський Транспорт", desc: "Основні маршрути автобусів/трамваїв на вул. Чорновола", time: "5 хв пішки" },
      { title: "Медицина", desc: "Клініки та аптеки", time: "3 хв" },
      { title: "Фітнес", desc: "Спортзали та фітнес-центри", time: "5 хв" },
      { title: "Парк Високий Замок", desc: "Зелена зона для прогулянок", time: "13 хв пішки" },
      { title: "Парк 700-річчя Львова", desc: "Популярний парк для активностей", time: "18 хв пішки" },
      { title: "Школи", desc: "Кілька шкіл поруч", time: "5-10 хв" },
      { title: "Дитячі Садки", desc: "3 дитячі садки в районі", time: "<5 хв пішки" }
    ],
    buildingInfo: [
      { label: "Адреса", value: "пр. Чорновола 16a, Львів, (Авалон Кільце 1)" },
      { label: "Поверховість Будинку", value: "9 поверхів" },
      { label: "Поверх Квартири", value: "7 поверх" },
      { label: "Клас Будинку", value: "Бізнес-клас" },
      { label: "Статус Квартири", value: "Як нова - Ми жили тут близько року (95% готова, залишилось кілька завершальних штрихів)" },
      { label: "Висота Стелі", value: "2.8м" },
      { label: "Вікна", value: "Великі панорамні вікна на південний схід" },
      { label: "Всього Квартир", value: "1000 квартир у комплексі" },
      { label: "Конструкція", value: "Монолітно-каркасна конструкція" },
      { label: "Утеплення", value: "Утеплення мінеральною ватою" },
      { label: "Опалення", value: "Автономне газове або електричне" },
      { label: "Стіни", value: "Керамоблок" },
      { label: "Безпека", value: "Цілодобова охорона та відеоспостереження" },
      { label: "Парковка", value: "Включене місце біля ліфта та виїзду" },
      { label: "Ліфт", value: "Сучасний швидкісний ліфт" }
    ],
    sections: {
      modernFeatures: {
        title: "Що Особливого",
        subtitle: "Розумні функції та комфорт у кожній деталі"
      },
      premiumBuilding: {
        title: "Сучасний Будинок та Чудове Розташування",
        subtitle: "Приєднуйтесь до спільноти в одному з найновіших житлових комплексів Львова",
        buildingExcellence: "Про Будинок",
        neighborhoodLife: "Що Поруч",
        rating: "Оцінка 4.9/5 від мешканців за якість життя та управління"
      },
      visualTour: {
        title: "Візуальний Тур",
        subtitle: "Автентичні фото реального повсякденного життя",
        gridView: "Сітка",
        carouselView: "Карусель",
        autoPlay: "Автопрогравання",
        stopAuto: "Зупинити",
        noImages: "Немає увімкнених зображень. Адміністратор може увімкнути зображення в панелі адміністратора."
      },
      aboutComplex: {
        title: "Про комплекс",
        videoCaption: "Це відео знайдено на YouTube (Автор Продаж квартир Львів)."
      },
      technicalPlans: {
        title: "Технічні Плани",
        subtitle: "Детальна архітектурна та технічна документація"
      },
      apartmentDetails: {
        title: "Що Всередині",
        subtitle: "Розумні функції для комфортного життя",
        climateHeating: "Клімат та Опалення",
        smartHomeSecurity: "Розумний Дім та Безпека",
        bathroom: "Ванна Кімната",
        kitchenAppliances: "Кухня та Побутова Техніка",
        interiorDesign: "Інтер'єр та Дизайн",
        utilitiesStorage: "Комунальні Послуги та Сховище",
        lifestyleWorkspace: "Стиль Життя та Робочий Простір"
      },
      thingsToKnow: {
        title: "Що Потрібно Знати",
        subtitle: "Важлива інформація про системи та можливості квартири",
        learnMore: "Дізнатися більше",
        showLess: "Згорнути",
        tabs: {
          operations: {
            label: "Експлуатація Квартири",
            rentalApproach: {
              title: "Наш Підхід до Оренди",
              content: [
                "Квартира пуста вже кілька років, тому ми не поспішаємо. Шукаємо правильних людей і готові чекати, скільки потрібно.",
                "Шукаємо відповідальних професіоналів з хорошими рекомендаціями та стабільною роботою. Це будувалось як наш власний дім з увагою до кожної деталі—не як орендна нерухомість.",
                "Сподіваємось, що орендарі будуть дбати про квартиру так само, як ми, ставлячись до неї як до свого дому. Мінімальний термін оренди — один рік.",
                "Комунальні послуги: Власник оплачує всі комунальні рахунки і виставляє вам рахунок на оплату. Це спрощує процес—вам не потрібно налаштовувати облікові записи з кількома постачальниками. Комунальні послуги зазвичай включають: електрику, газове опалення, воду, інтернет, плату за обслуговування будинку та моніторинг системи безпеки. Середня місячна вартість комунальних послуг залежить від сезону та використання, але очікуйте приблизно 2,000-4,000 грн взимку (опалювальний сезон) і 1,000-2,000 грн влітку."
              ]
            },
            woodenFloors: {
              title: "Система Опалення та Охолодження",
              content: [
                "У квартирі променеве опалення через стіни—сучасна технологія, енергоефективна і дуже комфортна.",
                "Чому це круто:",
                "Більше корисного простору: Немає громіздких батарей під вікнами. Опалення сховане в стінах, тож маєте повну свободу розставляти меблі.",
                "Комфортно при нижчій температурі: Тепло рівномірно йде від стін, створюючи природне тепло. Відчуваєте комфорт при 19-20°C, тоді як зі звичайними батареями потрібно 22-23°C.",
                "Чистіше повітря: Немає гарячих батарей—немає циркуляції пилу від конвекції. Добре для алергіків.",
                "Менші рахунки: Система працює при нижчих температурах (35-40°C проти 60-70°C для батарей), споживає менше енергії.",
                "Про дерев'яну підлогу:",
                "Вбудовані датчики автоматично контролюють підігрів підлоги (максимум 27°C), щоб дерево не висихало і не деформувалось. Система сама керує—будь ласка, не міняйте налаштування термостата підлоги."
              ]
            },
            bathroomWater: {
              title: "Гаряча Вода та Спа-Комфорт у Ванній",
              content: [
                "Ванна кімната відчувається як тепле спа цілий рік завдяки розумній системі опалення, яка думає наперед.",
                "Чому вам це сподобається:",
                "Тепла підлога щоразу після душу—навіть влітку. Система використовує опалення за таймером (ранок та вечір), яке нагріває підлогу, масажну лавку та вішалку для рушників. Коли виходите з душу, вас зустрічає тепла плитка замість холодної підлоги—незалежно від пори року чи того, чи ввімкнене опалення в будинку.",
                "Як це працює:",
                "Бак на 80 літрів з подвійним нагріванням (газ + електрика) забезпечує швидкий доступ до гарячої води та працює при відключенні світла. Газовий котел нагріває воду, а електричний елемент підтримує температуру, тож гаряча вода завжди готова.",
                "Контур опалення:",
                "Окремий контур бере тепло з бака для нагрівання трьох речей: підлоги ванної, вбудованої лавки з підігрівом та вішалки для рушників. Налаштовано за таймером на ранок та вечір, тож все готове, коли вам це найбільше потрібно.",
                "Корисно знати:",
                "• Якщо гарячою водою давно не користувались, почекайте 3-5 хвилин, поки газовий котел нагріє 80-літровий бак",
                "• Контур опалення потребує теплої води в баку + живлення для циркуляційного насоса",
                "• У резерві маєте 80 літрів теплої води навіть без електрики чи водопостачання",
                "Розумна Вентиляція:",
                "Три тихі витяжні вентилятори відстежують рівень вологості та вмикаються автоматично за потреби—підтримуючи свіже повітря та запобігаючи накопиченню вологи. Система настільки тиха, що ви ледь помічаєте її роботу. Ручні перемикачі дають контроль, коли вам це потрібно. Вентилятор у туалеті працює з розумним таймером затримки—він продовжує працювати ще кілька хвилин після вашого виходу, забезпечуючи повне очищення повітря без необхідності про це думати."
              ]
            },
            furnitureErgonomics: {
              title: "Продумана Ергономіка Меблів",
              content: [
                "Меблі розроблялися навколо реального життя—як дві людини насправді користуються простором разом, де речі природно накопичуються, і що робить дім легко організованим.",
                "Ранкова Рутина Стає Легшою:",
                "Дві людини готуються одночасно? Жодних проблем. Ванна кімната має великий умивальник з широкою бетонною стільницею, що забезпечує достатньо простору для двох. Косметика, туалетні речі, догляд за шкірою—є місце для всього, що використовуєте щодня.",
                "Але ось де це стає розумним: Виділена бічна шафа містить все, що вам потрібно час від часу, але не постійно. Зарядки для електричних зубних щіток, фен, інструменти для укладання—все зберігається вертикально на полицях з вбудованою розеткою. Вони доступні, але не захаращують ваш щоденний простір.",
                "Під умивальником дві глибокі шухляди тримають чотири кошики, що висуваються: два для свіжого одягу, два для речей, що чекають на прання. Одягайтесь одразу після душу без необхідності ходити мокрим по квартирі.",
                "Повернення Додому:",
                "Вхід спроектовано навколо того моменту, коли заходите через двері з повними руками. Зліва від входу: невелика полиця для розумного дисплею та ключів. Безпосередньо навпроти дверей: велика полиця на ідеальній висоті, щоб поставити сумку чи рюкзак. Більше ніяких «куди я поклав ключі?» моментів.",
                "Чистий Дизайн, Буквально:",
                "Кожен вимикач світла по всій квартирі—вхід, коридори, ванна, туалет—використовує датчики руху. Вони активуються автоматично, коли входите, і вимикаються, коли виходите. Окрім зручності, це означає відсутність брудних відбитків на вимикачах. Маленька деталь, яка тримає все чистішим.",
                "Кухня, Що Залишається Красивою:",
                "У відкритій студії кухня—частина вашого житлового простору. Ми були одержимі ідеєю тримати її привабливою без постійного прибирання.",
                "Стільниці глибиною 75 см забезпечують щедрий робочий простір. Планування тече природно: холодильник до мийки до зони підготовки до зони готування до зони сервірування до столу. Жодного марного руху.",
                "Щоденні речі—спеції, олії, часто вживані миски та тарілки—сидять на відкритих нижніх полицях у межах легкого доступу. Все інше ховається у закриті верхні шафи, що сягають стелі. Це служить двом цілям: менше накопичення пилу (немає верхівок шаф, що збирають бруд) та впорядкований вигляд, що завжди виглядає охайно.",
                "Результат:",
                "Це не просто дизайнерські рішення—це щоденні покращення якості життя. Менше часу на прибирання, менше ментального навантаження пам'ятати, куди що йде, більше часу насолоджуватись вашим простором."
              ]
            },
            electricalSystem: {
              title: "Електрика та Розумний Дім",
              content: [
                "У квартирі американські розетки та вимикачі (працюють від стандартних українських 240V). Сучасний вигляд плюс додаткові функції.",
                "Інтернет та Wi-Fi:",
                "Активний інтернет з Wi-Fi роутером на всю квартиру.",
                "Керування Розумним Домом:",
                "Керується через Philips Hue, Samsung SmartThings Hub та два термостати Google Nest (зараз в основному для моніторингу температури).",
                "Розумне Освітлення:",
                "Датчики руху: Світло у передпокої, ванній та туалеті вмикається автоматично при вході і вимикається після виходу.",
                "Wi-Fi вимикачі: Основні вимикачі у студії та спальні підключені до розумної системи. Працюють як звичайні вимикачі без Wi-Fi, але з розумним домом можете:",
                "• Вимикати все світло голосовими командами",
                "• Керувати світлом з телефону",
                "• Створювати автоматичні сценарії",
                "Розумний Замок (August Smart Lock):",
                "На вхідних дверях розумний замок, який полегшує життя:",
                "Авто-відкривання: Коли підходите з телефоном, двері відчиняються автоматично—не треба шукати ключі.",
                "Авто-зачинення: Двері самі зачиняються через кілька секунд після входу. Ніколи не забудете зачинити.",
                "Додаткова безпека: Є також звичайний механічний замок для підстраховки.",
                "Примітка: Авто-зачинення можна вимкнути, але воно працює надійно.",
                "Nest Video Doorbell:",
                "Отримуєте відеосповіщення на телефон при активності біля дверей. Можете розмовляти з відвідувачами, навіть коли вас немає.",
                "Універсальні Розетки:",
                "Більшість розеток підходять для різних вилок:",
                "• Європейські (Type C/F)",
                "• Українські",
                "• Американські (Type A/B)",
                "Примітка: Всі розетки 240V (український стандарт) незалежно від форми вилки.",
                "Перевага: Не потрібні адаптери для американських вилок (ноутбуки, зарядки)—якщо техніка підтримує 100-240V (більшість сучасної електроніки).",
                "Високопотужні Розетки: Окремі посилені розетки для пральної машини, бойлера та іншої потужної техніки з відповідними автоматами.",
                "Трансформатор 110V: Один трансформатор для рідкісної техніки, яка працює лише на американських 110V/60Hz.",
                "Розетки для Автоматизації:",
                "Розетки на терасі та біля вікон, готові для автоматичних ролет, якщо захочете їх додати пізніше."
              ]
            }
          },
          upgrades: {
            label: "Можливі Покращення",
            foodDispenser: {
              title: "Подрібнювач Відходів",
              content: [
                "На кухні є подрібнювач відходів під мийкою (подрібнює органічні відходи). Можливо потребує сервісу, але кран працює ідеально сам по собі.",
                "Із задоволенням покриємо ремонт, якщо захочете його налагодити."
              ]
            },
            washingMachine: {
              title: "Функція Сушки Пральної Машини",
              content: "Ми не користувались функцією сушки, бо працювала не стабільно. Якщо захочете перевірити з майстром, покриємо витрати на сервіс."
            },
            smartBlinds: {
              title: "Розумні Ролокасети",
              content: "Можна встановити розумні ролокасети на вікнах у спальні та в студії—як з внутрішньої, так і з зовнішньої сторони. Наприклад, контролер для рулонних штор Aqara Roller Shade Driver коштує близько 60$. Розетки вже встановлені біля вікон, готові для автоматизації. Із задоволенням покриємо витрати на встановлення, якщо захочете їх встановити."
            }
          },
          unfinished: {
            label: "Що Ще не Завершено",
            climateControl: {
              title: "Просунутий Клімат-Контроль",
              content: "Ще працюємо над повною системою клімат-контролю. Тепловий насос для кондиціонування ще не встановлений (забезпечував би охолодження плюс додаткове опалення до газового). Фанкоіли (кондиціонери), вбудовані в меблі, ще не працюють. Рекуперація повітря, центральний зволожувач та рідинне догрівання встановлені, але не підключені до термостатів. Це не обмежує функціональність квартири—опалення працює чудово, а розташування робить літо комфортним без кондиціонера."
            },
            bedroomLights: {
              title: "Настінне Світло у Спальні",
              content: "Настінне світло ще не встановлене—готові лише проводи в стіні. Із задоволенням покриємо витрати, якщо захочете встановити самі та включити в оренду."
            },
            bathroomMixer: {
              title: "Ручка Крана у Ванній",
              content: "Кран у ванній вбудований у бетонну стіну і працює ідеально, але потребує дизайнерської ручки для завершеного вигляду."
            }
          }
        }
      },
      infrastructure: {
        slides: [
          {
            title: "Все Необхідне Поруч",
            subtitle: "Прямо на місці",
            description: "Комплекс Avalon має чудовий комерційний простір з магазинами та сервісами для щоденного життя. Нова Пошта, Укрпошта, перукарні, салони краси, дитячий садок та кілька кав'ярень. Плюс продуктові магазини та аптеки—все в межах ходьби.",
            location: "Карта Інфраструктури",
            distance: "На місці"
          },
          {
            title: "Доступ до Центру Міста",
            subtitle: "Львівська Національна Опера",
            description: "Відчуйте культуру та атмосферу Львова. Avalon дає сучасне життя з легким доступом до центру міста. Лише 5 хвилин на авто і ви гуляєте біля Львівської Національної Опери, в самому серці культурної сцени міста.",
            location: "Львівська Національна Опера",
            distance: "1,5 кілометри"
          },
          {
            title: "ТРЦ New Point",
            subtitle: "Торговий центр New Point",
            description: "Торговий центр New Point—ключова частина району, де є все необхідне не виходячи з околиці. Цей комерційний хаб ідеально доповнює місцеву інфраструктуру.",
            location: "ТРЦ New Point",
            distance: "100 метрів"
          },
          {
            title: "Бізнес-Центр Penta",
            subtitle: "Бізнес-центр",
            description: "Сучасний бізнес-центр прямо в Avalon Kvartal. Стильний офісний простір з продуктивною атмосферою. Чудово для нетворкінгу чи роботи над проектами у надихаючому середовищі.",
            location: "Penta",
            distance: "10 метрів"
          },
          {
            title: "Парк 700-річчя",
            subtitle: "Зелена зона",
            description: "Головний парк Шевченківського району. Ідеально для прогулянок між робочими дзвінками чи часу з сім'єю та друзями. Чудово для вигулу собак, пробіжок, йоги або просто відпочинку на самоті.",
            location: "Парк 700-річчя",
            distance: "1,5 кілометри"
          }
        ]
      },
      contact: {
        title: "Зв'яжіться з Нами",
        subtitle: "Напишіть нам, щоб запланувати перегляд",
        responseTime: "Час відповіді: ~4 години",
        form: {
          nameLabel: "Ваше Ім'я",
          namePlaceholder: "Іван Іванчук",
          emailLabel: "Ваш Email",
          emailPlaceholder: "ivan@example.com",
          contactInfoLabel: "Як краще зв'язатися",
          contactInfoPlaceholder: "Email, телефон, Telegram, Facebook, WhatsApp тощо (наприклад, ivan@example.com, +380 12 345 6789, @telegram_username)",
          messageLabel: "Повідомлення",
          messagePlaceholder: "Ми молода пара. Ми щойно одружилися. У нас п'ятеро дітей, дві собаки, кіт і рибка. Я (Іван) працюю UX дизайнером вдома. Моя дружина Анна працює моїм босом. Хотіли б подивитися квартиру. Повідомте, коли вам зручно...",
          submit: "Надіслати Повідомлення",
          sending: "Відправка...",
          success: "Дякуємо! Ми отримали ваше повідомлення і скоро зв'яжемося.",
          error: "Щось пішло не так. Спробуйте ще раз або напишіть нам напряму."
        }
      },
      cta: {
        title: "Цікаво",
        title2: "Подивитися Квартиру?",
        subtitle: "Довгострокова оренда • Можливість покупки • Доступна зараз",
        subtitle2: "Напряму від власників • Без комісії агентства",
        emailCta: "Запланувати Перегляд через Email",
        telegram: "Або напишіть у Telegram",
        verified: "Напряму від Власників • Без Комісії • Доступна Зараз"
      }
    },
    apartmentDetails: {
      climateHeating: [
        { title: "Підлогове та Стінне Опалення", desc: "Променеве опалення через мікротрубки ефективно підтримує комфортну температуру" },
        { title: "Розумні Термостати", desc: "Інтелектуальний контроль температури для оптимального комфорту" },
        { title: "Дворежимний Бойлер", desc: "Газовий та електричний нагрів води" },
        { title: "Автономне Газове Опалення", desc: "Незалежна система газового опалення" }
      ],
      smartHomeSecurity: [
        { title: "Сейф", desc: "Вбудований сейф для цінностей" },
        { title: "Система Безпеки Ajax", desc: "Професійний моніторинг злому, захист від затоплення, пожежна сигналізація, автоматичне відключення при витоку газу" },
        { title: "Розумний Замок", desc: "Замок з авто-зачиненням та відкриттям через телефон" },
        { title: "Відеодзвінок Nest", desc: "Відеодзвінок зі сповіщеннями на телефон" },
        { title: "Розумні Вимикачі", desc: "Wi-Fi вимикачі з датчиками руху для автоматичного світла" },
        { title: "Універсальні Розетки", desc: "Розетки сумісні з ЄС/США по всій квартирі" },
        { title: "Швидкий Інтернет", desc: "Оптоволоконне з'єднання для швидкого, надійного інтернету" }
      ],
      bathroom: [
        { title: "Простора Ванна Кімната", desc: "Велика ванна кімната з душовою кабіною та ванною" },
        { title: "Масажна Лавка з Підігрівом", desc: "Вбудована лавка з підігрівом" },
        { title: "Розумна Сантехніка", desc: "Термостатичні змішувачі, тепле сидіння унітазу, сенсорне керування" }
      ],
      kitchenAppliances: [
        { title: "Комбінована Мікрохвильовка", desc: "Мікрохвильова піч з функцією конвекції" },
        { title: "Індукційна Плита", desc: "Сучасна індукційна варильна поверхня" },
        { title: "Сенсорний Кран", desc: "Доторкніться до крана, щоб увімкнути/вимкнути воду" },
        { title: "Фільтр Питної Води", desc: "Система зворотного осмосу" },
        { title: "Пральна/Сушильна Машина", desc: "2-в-1 пральна та сушильна машина" },
        { title: "Велика Посудомийна Машина", desc: "Простора посудомийна машина" },
        { title: "Вбудований Холодильник", desc: "Інтегрований у меблі на замовлення" }
      ],
      interiorDesign: [
        { title: "Дерев'яні Підлоги", desc: "Натуральна дерев'яна підлога у всіх житлових зонах" },
        { title: "Меблі на Замовлення", desc: "Ергономічно спроектовані меблі на замовлення" },
        { title: "Повністю Обставлена", desc: "Якісні меблі на замовлення по всій квартирі" },
        { title: "Дзеркала в Повний Зріст", desc: "Дзеркала від підлоги до стелі додають простору та світла" },
        { title: "Легкий у Догляді Дизайн", desc: "Поверхні, що миються, без плінтусів для легкого прибирання" },
        { title: "Кімната Сховище", desc: "Окремий простір для зберігання/технічна кімната" },
        { title: "Система Сортування Сміття", desc: "Організована система сортування для переробки" },
        { title: "Стіл Домашнього Офісу", desc: "Вбудований робочий стіл для віддаленої роботи" },
        { title: "Фітнес-Зона", desc: "Настінна конструкція для підтягувань та фітнес-обладнання" },
        { title: "Велика Тераса", desc: "Простора тераса з доступом з кожної кімнати, система поливу для рослин" },
        { title: "Якісне Ліжко та Диван", desc: "У спальні якісний матрац (жорсткий/напівжорсткий боки), у вітальні розкладний диван" }
      ],
      utilitiesStorage: [],
      lifestyleWorkspace: []
    },
    admin: {
      login: {
        title: "Доступ Адміністратора",
        placeholder: "Введіть пароль",
        login: "Увійти",
        cancel: "Скасувати",
        incorrectPassword: "Невірний пароль"
      },
      exit: "Вийти з Адміністратора",
      imageManagement: "Управління Зображеннями",
      enabled: "увімкнено",
      total: "всього",
      disable: "Вимкнути",
      enable: "Увімкнути",
      on: "ВКЛ",
      off: "ВИКЛ",
      videoPlaceholder: "Введіть шлях до відео файлу (наприклад, /complex_video.mp4)",
      noVideo: "Відео файл не встановлено. Адміністратор може додати шлях до відео файлу."
    },
    gallery: {
      view: "Перегляд",
      disableImage: "Вимкнути зображення",
      loadMore: "Показати",
      more: "більше",
      showing: "Показано",
      of: "з",
      fileName: "Ім'я файлу",
      comment: "Коментар",
      addComment: "Додати коментар",
      editComment: "Редагувати коментар",
      commentLine1: "Рядок 1",
      commentLine2: "Рядок 2",
      saveComment: "Зберегти",
      deleteComment: "Видалити"
    },
    lightbox: {
      close: "Закрити",
      previous: "Попереднє",
      next: "Наступне"
    },
    header: {
      logo: "Квартира",
      nav: {
        overview: "Огляд",
        gallery: "Галерея",
        details: "Деталі",
        plans: "Плани",
        contact: "Контакти"
      }
    },
    footer: {
      admin: "Адмін",
      exitAdmin: "Вийти з Адміністратора"
    },
    mobileContact: {
      telegram: "Telegram",
      email: "Email"
    },
    accessibility: {
      contactViaTelegram: "Зв'язатися через Telegram",
      previousImage: "Попереднє зображення",
      nextImage: "Наступне зображення",
      previousSlide: "Попередній слайд",
      nextSlide: "Наступний слайд",
      goToSlide: "Перейти до слайду",
      previousPlan: "Попередній план",
      nextPlan: "Наступний план",
      apartmentOverview: "Огляд квартири",
      telegramQrCode: "QR-код Telegram"
    }
  }
};

// Helper function to get nested translation values
export const getTranslation = (translations, language, key) => {
  const keys = key.split('.');
  let value = translations[language];
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return null;
    }
  }
  
  return value;
};

